import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faFingerprint } from "@fortawesome/free-solid-svg-icons";
import Switch from "react-switch";
import ApiService from "../../_services/ApiService";
import AuthenticationService from "../../_services/AuthenticationService";
import { StudentAttendanceDetail } from "../../_models/student-attendance-detail";
import { Student } from "../../_models/student";
import { AlertDialog } from "../../_components/alert-dialog/AlertDialog";
import { ConfirmDialog } from "../../_components/confirm-dialog/confirmDialog";
import "../attendance/attendance.css";

interface MatchParams {
  activitySlotId: string;
}

interface MyProps extends RouteComponentProps<MatchParams> {}

export const LaunchAttendance: React.FC<MyProps> = (props) => {
  const activitySlotId = props.match.params.activitySlotId;

  const [activitySlots, setActivitySlots] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [studentAttendanceDetails, setStudentAttendanceDetails] = useState<StudentAttendanceDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertTitle, setAlertTitle] = useState<string>("ALERT");
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [confirmMessage, setConfirmMessage] = useState<string>("");
  const [confirmTitle, setConfirmTitle] = useState<string>("CONFIRM");

  useEffect(() => {
    fetchActivitySlotData();
  }, [activitySlotId]);

  const fetchActivitySlotData = async () => {
    try {
      setLoading(true);

      // Fetch both activity slot data and students
      const [batchDataResponse, studentsResponse]: any[] = await Promise.all([
        ApiService.getActivitySlotBatchData(activitySlotId),
        ApiService.getActivitySlotStudents(activitySlotId),
      ]);

      console.log("Activity Slot Batch Data:", batchDataResponse.data);
      console.log("Activity Slot Students:", studentsResponse.data);

      // The batch data response is an array of activity slots (one per batch)
      const batchDataArray = batchDataResponse.data?.data || batchDataResponse.data || [];

      // Store all activity slots
      const slotsArray = Array.isArray(batchDataArray) ? batchDataArray : [batchDataArray];
      setActivitySlots(slotsArray);

      // Extract students from the students response
      const studentsList = studentsResponse.data?.data || studentsResponse.data || [];
      setStudents(studentsList);

      // Initialize attendance details - map the API structure to our Student model
      const attendanceDetails: StudentAttendanceDetail[] = studentsList.map((apiStudent: any) => ({
        student: {
          id: apiStudent.enrollmentId,
          name: apiStudent.studentName,
          studentCode: apiStudent.studentCode,
          batchId: apiStudent.batchId,
        },
        attendanceStatus: true,
        bioMetricAttendnace: null,
      }));

      setStudentAttendanceDetails(attendanceDetails);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching activity slot data:", error);
      setAlertMessage("Failed to fetch activity slot data. Please try again.");
      setShowAlert(true);
      setLoading(false);
    }
  };

  const handleAttendanceSwitch = (index: number) => {
    const updatedDetails = [...studentAttendanceDetails];
    updatedDetails[index].attendanceStatus = !updatedDetails[index].attendanceStatus;
    setStudentAttendanceDetails(updatedDetails);
  };

  const handleAllPresent = () => {
    const updatedDetails = studentAttendanceDetails.map((detail) => ({
      ...detail,
      attendanceStatus: true,
    }));
    setStudentAttendanceDetails(updatedDetails);
  };

  const handleAllAbsent = () => {
    const updatedDetails = studentAttendanceDetails.map((detail) => ({
      ...detail,
      attendanceStatus: false,
    }));
    setStudentAttendanceDetails(updatedDetails);
  };

  const saveAttendance = async () => {
    try {
      const currentUser = AuthenticationService.currentUserValue;

      // Extract unique batch IDs from students
      const batchIds = Array.from(new Set(
        studentAttendanceDetails.map(detail => detail.student.batchId)
      ));

      const attendancePayload = {
        batchIds: batchIds,
        studentAttendanceDetails: studentAttendanceDetails,
      };

      console.log("Saving attendance:", attendancePayload);

      // Call the API to save attendance
      const response = await ApiService.saveActivitySlotAttendance(
        parseInt(activitySlotId),
        attendancePayload
      );

      if (response.data.status === 0) {
        setAlertMessage(response.data.message);
        setShowAlert(true);
        return;
      }

      // Calculate present/absent counts
      const presentCount = studentAttendanceDetails.filter((d) => d.attendanceStatus).length;
      const absentCount = studentAttendanceDetails.length - presentCount;

      const message = `<b>Attendance saved successfully.</b><br/>Number of Students Present: <b style='color:green'>${presentCount}</b><br/>Number of Students Absent: <b style='color:red'>${absentCount}</b>`;

      setConfirmMessage(message);
      setConfirmTitle("ATTENDANCE SAVE SUCCESS");
      setShowConfirm(true);
    } catch (error) {
      console.error("Error saving attendance:", error);
      setAlertMessage("Failed to save attendance. Please try again.");
      setShowAlert(true);
    }
  };

  const handleConfirm = (flag: boolean) => {
    setShowConfirm(false);
    if (flag) {
      props.history.goBack();
    }
  };

  const handleAlert = (flag: boolean) => {
    setShowAlert(false);
  };

  const _dismiss = () => {
    props.history.goBack();
  };

  if (loading) {
    return (
      <div className="attendance">
        <div className="app flex-row align-items-center">
          <div className="container">
            <div className="text-center">
              <h4>Loading...</h4>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance">
      {showAlert && (
        <AlertDialog
          message={alertMessage}
          title={alertTitle}
          btnOkText="OK"
          size="lg"
          close={handleAlert}
        />
      )}
      {showConfirm && (
        <ConfirmDialog
          message={confirmMessage}
          title={confirmTitle}
          btnOkText="OK"
          btnCancelText="CANCEL"
          handleResult={handleConfirm}
          size="lg"
        />
      )}
      <div className="app flex-row align-items-center">
        <div className="container">
          <div className="card-group">
            <div className="card text-center border-info full-height1">
              <div className="card-header pageheading bg-info text-white">
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  size="lg"
                  className="float-left"
                  onClick={_dismiss}
                />
                <b>Launch Attendance</b>
              </div>
              <div className="card-body">
                {/* Activity Slots Info */}
                {activitySlots.length > 0 && (
                  <div className="row">
                    <div className="col text-left">
                      {activitySlots.map((slot, index) => {
                        // Filter students for this batch
                        const batchStudents = studentAttendanceDetails.filter(
                          (detail) => detail.student.batchId === slot.batchId
                        );

                        return (
                          <div key={index} style={{
                            marginBottom: '20px',
                            padding: '15px',
                            border: '2px solid var(--brand-primary)',
                            borderRadius: '8px',
                            backgroundColor: '#f9f9f9'
                          }}>
                            <h6 style={{ color: 'var(--brand-primary)', marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>
                              Batch #{index + 1}: {slot.batchName || `Batch ${slot.batchId}`}
                            </h6>
                            <div style={{ marginBottom: '10px' }}>
                              <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                <strong>Template:</strong> {slot.timeTableTemplateName || "N/A"}
                              </p>
                              <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                <strong>Instructor:</strong> {slot.instructorName || "N/A"}
                              </p>
                              <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                <strong>Date:</strong> {slot.templateDate || "N/A"}
                              </p>
                              <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                <strong>Time:</strong> {slot.startTime || "N/A"} - {slot.endTime || "N/A"}
                              </p>
                              {slot.meetingData && (
                                <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                  <strong>Topic:</strong> {slot.meetingData.topic || "N/A"}
                                </p>
                              )}
                            </div>

                            {/* Student count for this batch */}
                            {batchStudents.length > 0 && (
                              <div style={{
                                marginTop: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid var(--border-default)'
                              }}>
                                <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold', color: 'var(--brand-primary)' }}>
                                  Total Students: {batchStudents.length}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Mark All Buttons */}
                {studentAttendanceDetails.length > 0 && (
                  <div className="row mark-all">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={handleAllPresent}
                    >
                      Mark All Present
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={handleAllAbsent}
                    >
                      Mark All Absent
                    </button>
                  </div>
                )}

                <br />

                {/* Student Attendance List */}
                {studentAttendanceDetails.map((attendanceDetail, i) => (
                  <div key={i}>
                    <div className="row">
                      <div className="col col-auto align-self-center align-items-center">
                        <label>
                          <Switch
                            checkedIcon={true}
                            uncheckedIcon={true}
                            className="react-switch"
                            checked={attendanceDetail.attendanceStatus}
                            onChange={() => handleAttendanceSwitch(i)}
                          />
                        </label>
                      </div>
                      <div className="col text-left align-self-center align-items-center">
                        <span>
                          {attendanceDetail.student.studentCode}
                          <br />
                          {attendanceDetail.student.name}
                        </span>
                        {(attendanceDetail.bioMetricAttendnace === null ||
                          attendanceDetail.bioMetricAttendnace === undefined) && (
                          <FontAwesomeIcon
                            icon={faFingerprint}
                            size="lg"
                            className="float-right text-muted"
                          />
                        )}
                        {attendanceDetail.bioMetricAttendnace === true && (
                          <FontAwesomeIcon
                            icon={faFingerprint}
                            size="lg"
                            className="float-right text-secondary"
                          />
                        )}
                        {attendanceDetail.bioMetricAttendnace === false && (
                          <FontAwesomeIcon
                            icon={faFingerprint}
                            size="lg"
                            className="float-right text-danger"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Save Button */}
                {studentAttendanceDetails.length > 0 && (
                  <div className="row">
                    <button
                      type="button"
                      className="btn btn-primary btn-lg btn-block"
                      onClick={saveAttendance}
                    >
                      SAVE ATTENDANCE
                    </button>
                  </div>
                )}

                {studentAttendanceDetails.length === 0 && (
                  <div className="row">
                    <div className="col text-center">
                      <p>No students found for this activity slot.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
